import { StripeElementLocale } from '@stripe/stripe-js';
import { removeWhiteSpace } from '../helpers/string-utils';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import moment from 'moment';

export const APP_VERSION = '0.0.110';

//for general
export const BRAND = 'YYC taxPOD';
export const APPLICATION_NAME = 'taxPOD';
export const WHATS_APP_CONTACT_PRIMARY_DISPLAY = '016-507 7309';
export const WHATS_APP_CONTACT_SECONDARY_DISPLAY = '016-624 7309';
export const CUSTOMER_SUPPORT_EMAIL = 'support@taxpod.my';
export const CURRENCY = 'MYR';
export const COUNTRY_DIAL_CODES = ['my', 'sg'];
export const MALAYSIA_PHONE_COUNTRY_CODE = '+60';
export const WHATSAPP_MAIN_API_LINK = 'https://api.whatsapp.com/send?phone=';
export const CUSTOMER_SUPPORT_WHATSAPP_LINK = `${WHATSAPP_MAIN_API_LINK}${`6${removeWhiteSpace(WHATS_APP_CONTACT_PRIMARY_DISPLAY).replace('-', '')}`}`;
export const NON_BREAKING_SPACES = '\u00A0';

//pattern or regex related
export const DIGIT_ONLY_PATTERN = '[0-9]/*';
export const AT_LEAST_ONE_NUMBER_PATTERN = /[0-9]/;
export const AT_LEAST_A_LOWERCASE_PATTERN = /[a-z]/;
export const AT_LEAST_A_UPPERCASE_PATTERN = /[A-Z]/;
export const AT_LEAST_A_SPECIAL_CHAR_PATTERN = /[!@#$%^&*(),.?":{}|<>\[\]\\\/'`;~\-=_+]/;

//date format
export const COURSE_DATE_FORMAT = 'dd MMM yyyy';
export const TWENTY_FOUR_HOURS_FORMAT = 'HH:mm';
export const DATE_TIME_FORMAT = 'dd MMM yyyy, HH:mm';
export const TWELVE_HOURS_FORMAT = 'hh:mm a';
export const PUBLISHED_DATE_FORMAT = 'd MMM yyyy';

//search
export const SEARCH_MAX_LENGTH = 256;

//screen size
export const SCREEN_WIDTH_MOBILE = 450;
export const SCREEN_WIDTH_SMALL = 640;
export const SCREEN_WIDTH_MEDIUM = 768;
export const SCREEN_WIDTH_DESKTOP = 1024;

export const MY_TEL_INPUT_SIZE = 12;
export const MY_DIAL_CODE = 'my';
export const GLOBAL_TEL_INPUT_SIZE = 15;

export const MINUTES = 1000 * 60;
export const HOURS = MINUTES * 60;
export const DAYS = HOURS * 24;

export const TODAY_IN_UTC = moment().startOf('day').utc().toISOString();
export const NOW_IN_UTC = moment().utc().toISOString();
export const THIRTEEN_DAYS_FROM_NOW_IN_UTC = moment().add(13, 'days').utc().toISOString();

//debounce time
export const DEBOUNCE_TIME_FAST = 300;
export const DEBOUNCE_TIME_MEDIUM = 500;
export const DEBOUNCE_TIME_SLOW = 1000;

// Image placeholder
export const GENERAL_IMAGE_PLACEHOLDER = './assets/images/placeholder/general-placeholder-image.jpg';
export const GENERAL_IMAGE_TRANSPARENT_BG_PLACEHOLDER =
    './assets/images/placeholder/general-placeholder-transparent-bg.png';

// ERROR CODE
export const ERR_FAILED_PROGRAM_REGISTRATION = 'ERR_FAILED_PROGRAM_REGISTRATION';
export const AUTHORIZATION_REQUIRED_NEW_DEVICE_LOGIN_DETECTED = 'AUTHORIZATION_REQUIRED_NEW_DEVICE_LOGIN_DETECTED';
export const ERR_ACCOUNT_LOCKED = 'ERR_ACCOUNT_LOCKED';
export const ERR_REQUEST_RECENTLY = 'ERR_REQUEST_RECENTLY';
export const ERR_PASSWORD_EMPTY = 'ERR_PASSWORD_EMPTY';
export const ERR_USER_INSUFFICIENT_AAE_MINUTES = 'ERR_USER_INSUFFICIENT_AAE_MINUTES';
export const ERR_USER_AAE_BANNED_SUBSCRIPTION_ENDING = 'ERR_USER_AAE_BANNED_SUBSCRIPTION_ENDING';
export const ERR_USER_EXCEEDED_DEVICE_LIMIT = 'ERR_USER_EXCEEDED_DEVICE_LIMIT';
export const ERR_DEVICE_REVOKE_OTP_EXPIRED = 'ERR_DEVICE_REVOKE_OTP_EXPIRED';
export const ERR_DEVICE_REVOKE_OTP_INVALID = 'ERR_DEVICE_REVOKE_OTP_INVALID';
export const ERR_RESEND_EMAIL_COOLDOWN = 'ERR_RESEND_EMAIL_COOLDOWN';
export const ERR_OTP_VERIFICATION_RATE_LIMIT_EXCEEDED = 'ERR_OTP_VERIFICATION_RATE_LIMIT_EXCEEDED';
export const ERR_USER_ALREADY_COMPLETE_EMAIL_VERIFY = 'ERR_USER_ALREADY_COMPLETE_EMAIL_VERIFY';
export const ERR_ASKPOD_CONNECT_FAILED = 'ERR_ASKPOD_CONNECT_FAILED';
export const ERR_ASKPOD_NOT_ELIGIBLE = 'ERR_ASKPOD_NOT_ELIGIBLE';

// Device removal redirect delay (in milliseconds)
export const DEVICE_REMOVAL_REDIRECT_DELAY = 5000; // 5 seconds

export const FILE_SIZE_BYTES = 1024;

export const LOCAL_TRANSLATION_LANGUAGE = {
    EN: 'en-US',
    EN_GB: 'en-GB',
    CH: 'zh-CN'
};

export const STRIPE_LOCAL_TRANSLATION_LANGUAGE = {
    'en-US': 'en',
    'zh-CN': 'zh',
};

export const SERVER_TRANSLATION_LANGUAGE = {
    'en-US': 'en',
    'zh-CN': 'ch',
};

export const TRANSLATION_ABBREVIATION_LANGUAGE = {
    'English': 'enum.programLanguage.english',
    'Chinese': 'enum.programLanguage.mandarin',
};

export const DEFAULT_EDITOR_CONFIG: AngularEditorConfig = {
    defaultFontSize: '3',
    editable: true,
    enableToolbar: true,
    showToolbar: true,
    sanitize: false,
};

export function getLocalLanguagesInValueArray(): string[] {
    return Object.values(LOCAL_TRANSLATION_LANGUAGE);
}

export function getLocalLanguagesInKeyArray(): string[] {
    return Object.keys(LOCAL_TRANSLATION_LANGUAGE);
}

export function getLocalLanguageInValueArray(value: string): string|null {
    return Object.values(LOCAL_TRANSLATION_LANGUAGE).find((item) => item === value) ?? null;
}

export function getLocalLanguageInKeyArray(key: string): string|null {
    return Object.keys(LOCAL_TRANSLATION_LANGUAGE).find((item) => item === key) ?? null;
}

export function getStripeLocalTranslation(key: string): StripeElementLocale {
    return STRIPE_LOCAL_TRANSLATION_LANGUAGE[
        key as keyof typeof STRIPE_LOCAL_TRANSLATION_LANGUAGE
    ] as StripeElementLocale;
}

export function getServerLocalTranslation(key: string): string {
    return SERVER_TRANSLATION_LANGUAGE[
        key as keyof typeof SERVER_TRANSLATION_LANGUAGE
    ] ?? '';
}

export function getTranslationLanguageFromFullName(languageName: string): string {
    return TRANSLATION_ABBREVIATION_LANGUAGE[
        languageName as keyof typeof TRANSLATION_ABBREVIATION_LANGUAGE
    ] ?? '';
}
